import { scrollPageTo } from "utils";

import { SCENARIO_SCROLL } from "consts";

const isVerticallyScrollable = (element: HTMLElement): boolean => {
  const { overflowY } = window.getComputedStyle(element);
  if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay") return false;
  return element.scrollHeight > element.clientHeight + 1;
};

/** The nearest element that scrolls vertically: `.ant-layout-content` on the results page, else the document. */
export const getScrollParent = (node: HTMLElement | null): HTMLElement => {
  let current = node;
  while (current && current !== document.documentElement) {
    if (isVerticallyScrollable(current)) return current;
    current = current.parentElement;
  }
  return document.documentElement;
};

const isDocumentScroll = (container: HTMLElement): boolean => container === document.documentElement;

/** Where a container's scroll events fire and its scroll calls go: the window when the document scrolls. */
export const getScrollTarget = (container: HTMLElement): HTMLElement | Window => (isDocumentScroll(container) ? window : container);

/** How far the container is scrolled; the document reports it on the window. */
export const getScrollTop = (container: HTMLElement): number => (isDocumentScroll(container) ? window.scrollY : container.scrollTop);

/** Scrolls whichever element scrolls `element` by `top` px. */
export const scrollParentBy = (element: HTMLElement, top: number, behavior: ScrollBehavior) => {
  getScrollTarget(getScrollParent(element.parentElement)).scrollBy({ top, behavior });
};

/** Top of the container's visible area in viewport coordinates; the document scrolls from 0. */
export const getContainerTop = (container: HTMLElement): number => (isDocumentScroll(container) ? 0 : container.getBoundingClientRect().top);

/** Scrolls whichever element scrolls `element` so its top sits `offset` px below that container's top. */
export const scrollToElement = (element: HTMLElement, offset: number, behavior: ScrollBehavior = "smooth") => {
  const container = getScrollParent(element.parentElement);
  const elementTop = element.getBoundingClientRect().top;
  if (isDocumentScroll(container)) {
    scrollPageTo(window.scrollY + elementTop - offset, behavior);
  } else {
    container.scrollTo({ top: container.scrollTop + (elementTop - getContainerTop(container)) - offset, behavior });
  }
};

/** Whether `element` sits at `offset` below its scroll container's top (not the viewport's), within the alignment tolerance. */
export const isAlignedTo = (element: HTMLElement | null, offset: number): boolean => {
  if (!element) return false;
  const containerTop = getContainerTop(getScrollParent(element.parentElement));
  return Math.abs(element.getBoundingClientRect().top - containerTop - offset) <= SCENARIO_SCROLL.ALIGNMENT_TOLERANCE_PX;
};
